import { createFileRoute } from "@tanstack/react-router";
import { lazy, Suspense } from "react";
import { breadcrumbSchema, collectionSchema } from "@/lib/schema";
import { OFFICIAL_CARD_COUNT } from "@/lib/seo-snapshots";
import { OFFICIAL_CARDS } from "@/lib/tcg-cards";

const CardsContent = lazy(() => import("@/components/pages/CardsContent").then((m) => ({ default: m.CardsContent })));

const SHARE_IMAGE =
  "https://pub-bb2e103a32db4e198524a2e9ed8f35b4.r2.dev/17714a28-4a57-4e59-8359-909140a1f8f4/id-preview-3bac5354--a3729fa2-e85b-4f5b-84b2-0934e880c580.lovable.app-1785103564262.png";

const CARD_FAQS = [
  { q: "How many cards are in the Palworld Official Card Game?", a: `The Palworld OFFICIAL CARD GAME currently has ${OFFICIAL_CARD_COUNT} printed cards indexed on Pal Stars: 100 base cards in BP01 Dawn of Palpagos plus its SR, SP, OSR, SSP and SSS parallels, 24 cards each in the TD01 Red・Blue and TD02 Green・Purple Trial Decks with TSR and TSP parallels, and promo (PR) cards.` },
  { q: "Where is the card number printed on a Palworld TCG card?", a: "The card number sits in the lower band of the card face and reads as set code, dash, collector number — for example EBP01-001. Parallel printings append their rarity to the same number, so the OSR parallel of EBP01-001 is printed EBP01-001OSR and the Super Special Parallel is printed EBP01-001SSP." },
  { q: "What do the Palworld card rarity codes C, U, R, RR, SR, SP, OSR, SSP and SSS mean?", a: "C is Common, U is Uncommon, R is Rare, RR is Double Rare, SR is Super Rare, SP is a Special foil parallel, OSR is an Over Super Rare with exclusive illustration, SSP is a Super Special Parallel with textured full-bleed art, and SSS is the single Ultra Secret Gold Card chase card. Trial Deck printings use TD, TSR and TSP, and promos use PR." },
  { q: "What do Palworld card colors mean?", a: "Palworld cards use Red, Blue, Green, Purple and Colorless. A card's color determines which energy it needs and which deck archetypes it fits; Colorless cards splash into any deck." },
  { q: "How do I verify a Palworld card number?", a: "Use the Pal Stars Card Checker: enter the printed code such as EBP01-001 or EBP01-001SSP and compare the returned set, rarity, color, cost and card art with the card in hand." },
  { q: "Palworld TCG card database checklist?", a: "Use the card database as a Palworld TCG card database checklist: filter by set, card type, element, color and rarity, then compare each printed card number against your binder." },
  { q: "Official Palworld trading card list by element?", a: "The official Palworld trading card list by element can be built from the advanced filters: choose an element, then sort or narrow by rarity, color and set." },
  { q: "Palworld card game rules and deck building?", a: "Start with the TCG Tools page for Palworld card game rules and deck building support, then use this database to verify each card's color, cost, power and printed rules text." },
];


function CardsPending() {
  return (
    <div className="mx-auto max-w-7xl px-4 sm:px-6 py-10">
      <h1 className="text-3xl sm:text-4xl font-bold">Palworld Card Database</h1>
      <p className="aeo-answer mt-2 max-w-3xl text-muted-foreground">Browse every Palworld OFFICIAL CARD GAME card by exact printed number, rarity, color, cost and power.</p>
      <section className="mt-14 max-w-3xl">
        <h2 className="text-xl font-bold">Palworld card database FAQ</h2>
        <div className="mt-4 space-y-4">
          {CARD_FAQS.map((f) => (
            <div key={f.q} className="rounded-xl border bg-card/40 p-4">
              <h3 className="font-semibold">{f.q}</h3>
              <p className="aeo-answer mt-1.5 text-sm text-muted-foreground leading-relaxed">{f.a}</p>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}

function RouteComponent() {
  return <Suspense fallback={<CardsPending />}><CardsContent /></Suspense>;
}

export const Route = createFileRoute("/cards/")({
  pendingComponent: CardsPending,
  component: RouteComponent,
  head: () => ({
    meta: [
      { title: "Palworld TCG Cards: Full Card List, Rarity & Value" },
      { name: "description", content: `All ${OFFICIAL_CARD_COUNT} Palworld TCG cards in one searchable list — official scans, printed numbers (EBP01, ETD01, EPR), rarity codes, colors and live market values.` },
      { property: "og:title", content: "Palworld TCG Cards — Full Card List & Values" },
      { property: "og:description", content: "Every official Palworld TCG card with exact card numbers, rarity codes and card images." },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "https://palstars.com/cards" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: "Palworld Card Database — Pal Stars" },
      { name: "twitter:description", content: "Every official Palworld TCG card with exact card numbers and rarity codes." },
      { property: "og:image", content: SHARE_IMAGE },
      { name: "twitter:image", content: SHARE_IMAGE },
    ],
    links: [{ rel: "canonical", href: "https://palstars.com/cards" }],
    scripts: [
      breadcrumbSchema([["Home", "/"], ["Card Database", "/cards"]]),
      collectionSchema({
        name: "Palworld TCG Card Database",
        description: `A complete directory of all ${OFFICIAL_CARD_COUNT} printed Palworld OFFICIAL CARD GAME cards with official scans, rarity codes and market links.`,
        path: "/cards",
        items: OFFICIAL_CARDS.filter((c) => !c.variant).map((c) => ({ name: `${c.code} ${c.name}`, path: `/cards/${c.code}` })),
        numberOfItems: OFFICIAL_CARD_COUNT,
      }),
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          speakable: { "@type": "SpeakableSpecification", cssSelector: [".snippet-question", ".snippet-answer"] },
          mainEntity: CARD_FAQS.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Dataset",
          name: "Pal Stars Palworld Official Card Game Database",
          description: "Every printed Palworld OFFICIAL CARD GAME card indexed by exact card number, set, rarity code, color, cost and power.",
          url: "https://palstars.com/cards",
          keywords: ["Palworld cards", "Palworld Official Card Game", "Palworld card numbers", "Palworld TCG rarity", "Pal Stars"],
          creator: { "@type": "Organization", name: "Pal Stars", url: "https://palstars.com" },
          license: "https://creativecommons.org/licenses/by-nc/4.0/",
          isAccessibleForFree: true,
        }),
      },
    ],
  }),
});
